import { useLazyQuery, useMutation } from '@apollo/client';
import { embed } from '@blackbird/common/embedding';
import { Close as CloseIcon, Send as SendIcon } from '@mui/icons-material';
import {
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import * as Schema from 'generated/graphql/schema';
import { FunctionComponent, useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';

import TextContentArea from '@/components/containers/text-content-area';
import AndonCallDialogHeader from '@/components/dialogs/andon/header';
import { Context } from '@/contexts/company';
import { andonCallRelease, andonCallWorkOrderInitiate } from '@/graphql/mutations';
import { andonListWorkOrderKeys } from '@/graphql/queries';
import { buildSummary } from '@/helpers/andon';
import { guardedCallback } from '@/helpers/helper-functions';
import { useUtils } from '@/hocs';

const HISTORY_NOTE_LIMIT = 1500;

const useStyles = makeStyles()((theme) => ({
  iconSizeLeft: {
    color: 'inherit',
    height: '1em !important',
    width: '1em !important',
    fontSize: theme.typography.pxToRem(24),
    marginRight: theme.spacing(),
  },
  iconSizeRight: {
    color: 'inherit',
    height: '1em !important',
    width: '1em !important',
    fontSize: theme.typography.pxToRem(24),
    marginLeft: theme.spacing(),
  },
  buttonWithIconRight: {
    '& > span > svg': {
      marginLeft: theme.spacing(),
    },
  },
  buttonWithIconLeft: {
    '& > span > svg': {
      marginRight: theme.spacing(),
    },
  },
  buttonNegative: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.getContrastText(theme.palette.error.main),
    '&:hover': {
      backgroundColor: theme.palette.error.main,
    },
  },
  hideWhileSmall: {
    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
  },
}));

export interface Properties {
  call: Schema.AndonListCallsQuery['andon'][0]['calls'][0];
  onClose(_: unknown): void;
}

const errorCodes = {
  valby: {
    symptomValues: [
      'ANDET',
      'ELEKTRISK',
      'EMBALLAGE',
      'FORSYNING',
      'KALD ANNULLERES',
      'KOMPONENT DEFEKT',
      'KOMPONENT LØS',
      'KOMPONENT STRAM',
      'KOMPONENT TILSTOP.',
      'KOMPONENT UTÆT',
      'MEKANISK',
      'NEDSAT YDELSE',
      'OPERATØR SUPPORT',
      'PRODUKT',
      'PRODUKTIONSFORHOLD',
      'STØJ/VIBRATIONER',
      'UDE AF JUSTERING',
    ],
    causeValues: [
      'AMPUL',
      'ANDET',
      'BLOKERET',
      'BOOKLET',
      'BRANDALARM',
      'BRUGSER',
      'DESIGN',
      'DRÅBER',
      'DÆKFOLIE',
      'EKSTERNE FORHOLD',
      'EKSTERNT LYS',
      'F-KASSEETIKET',
      'F-KASSER',
      'FEJL IKKE OBSERV.',
      'FEJLBETJENING',
      'FEJLJUSTERING',
      'FORMFOLIE',
      'FRIKTION',
      'FUGT',
      'FØLERFEJL',
      'IT/SAP',
      'KAPSEL',
      'KARTONETIKET',
      'KARTONNER',
      'KORTSLUTNING',
      'KULDE',
      'KØLEVAND',
      'MANGLENDE OPLÆRING',
      'MANGLENDE RENGØR.',
      'MATERIALE / MEDIE',
      'OMSTILLINGSFEJL',
      'OMSTILLINGSHJÆLP',
      'OPERATØR FEJLRETN.',
      'OVERBELASTET',
      'PIPETTER',
      'PRODUKTETIKET',
      'PROGRAMFEJL',
      'SLITAGE',
      'STRÆKFOLIE',
      'STRØM',
      'STYRESYSTEM',
      'STØJ',
      'STØV / SNAVS',
      'TABLET',
      'TEMPERATUR',
      'TILSTOPNING',
      'TRYK',
      'UDSUGNING',
      'VARME',
      'VENTILATION',
      'VIALS',
      'VIBRATIONER',
      'VISION',
    ],
    actionValues: [
      'ANDET',
      'DRIFTJUSTERING',
      'GENINDLÆST FORMAT',
      'GRUNDJUSTERING',
      'MASKINE GENSTARTET',
      'NØDREPARERET',
      'OPLÆRING',
      'OVERVÅGNING',
      'RENGJORT',
      'REPARERET',
      'SMØRING',
      'SOFTWAREOPDATERING',
      'UDSKIFTNING',
    ],
  },
  valbonne: {
    symptomValues: [
      'ALIGNEMENT COMPRIMES',
      'ALIM. CHEVALETS',
      'ALIM. ETUIS ETUYEUSE',
      'ALIM. ETUIS VIGNETTEUSE',
      'ALIM. FLACONS',
      'ALIM. PANNEAUX',
      'ALIMENTATION CARTONS',
      'ALIMENTATION ELECTRIQUE',
      'ALIMENTATION FARDEAUX',
      'ALIMENTATION PNEUMATIQUE',
      'ALIMENTATION PRODUIT',
      'BUG SYSTEME',
      'CENTRAGE IMPRESSION',
      'CHARGEMENT BLISTERS',
      'CLOTURE LOT',
      'CODE ARTICLE',
      'COLLAGE CHEVALETS',
      'COLLAGE COUVERCLE',
      'COLLAGE ETUIS',
      'COLLAGE PANNEAUX',
      'CONNEXION',
      'CONTROLE CHEVALETS',
      'CONTROLE ETUIS M1',
      'CONTROLE ETUIS M9',
      'CONTROLE IMPRESSION',
      'CONTROLE LANGUETTES',
      'CONTROLE PANNEAUX',
      'CONTROLE PRESENCE COMPRIMES',
      'CONTROLE VIGNETTES',
      'CONTROLES FLACONS',
      'CONTROLES T&T',
      'DECOUPE / PRE-DECOUPE/PRE-CASS',
      'DEPOSE ETIQUETTES FARDEAUX',
      'DEPOSE ETIQUETTES GROUPAGE',
      'DEPOSE ETIQUETTES PRODUIT',
      'DEPOSE ETIQUETTES T&T',
      'DEPOSE VIGNETTES',
      'DEROULEMENT ALUMINIUM',
      'DEROULEMENT FILM',
      'DEROULEMENT PVC',
      'EJECTION BLISTERS',
      'EJECTION ETUIS',
      'EJECTION FARDEAUX',
      'EMPILEMENT ETUIS',
      'ENCAISSAGE FARDEAUX',
      'ENTRAINEMENT PRINCIPAL SYNCHRO',
      'ENTREE FARDEAUX',
      'FERMETURE CARTONS',
      'FERMETURE ETUIS',
      'FORMAGE',
      'GESTION FORMAT',
      'IMPRESSION ETIQUETTES',
      'IMPRESSION ETUIS',
      'IMPRESSION HAPA',
      'INTRODUCTION BLISTERS',
      'INTRODUCTION FARDEAUX',
      'LANCEMENT DONNEES',
      'LECTURE CODES',
      'MAGASIN NOTICES',
      'MIRAGE FLACONS',
      'MISE EN FORME ETUIS',
      'PALETTISATION CARTONS',
      'PESEE ETUIS',
      'PLIAGE NOTICES',
      'PRECHAUFFAGE',
      'PRISE NOTICES',
      'REFROIDISSEMENT',
      'REMPLISSAGE COMPRIMES',
      'SCELLAGE',
      'SCELLAGE FARDEAUX',
      'SECURITE MACHINE',
      'SORTIE ETUIS TRIEUSE',
      'SORTIE ETUIS VIGNETTEUSE',
      'SORTIE FLACONS',
      'SORTIE MACHINE',
      'SORTIE PRODUIT',
      'STABILISATION',
      'TIRAGE LIGNE',
      'TRANSFERT NOTICES',
      'TRANSFERT PRODUIT',
      'TRANSOORT FLACONS',
      'TRANSPORT BLISTERS',
      'TRANSPORT CARTONS CHAINES',
      'TRANSPORT CARTONS ROULEAUX',
      'TRANSPORT ETUIS ETUYEUSE',
      'TRANSPORT ETUIS FARDELEUSE',
      'TRANSPORT ETUIS VIGNETEUSE',
      'TRANSPORT FARDEAUX PALETTISEUR',
      'TRANSPORT FARDEAUX TABLE ACCU.',
      'TRANSPORT NOTICES',
    ],
    causeValues: [],
    actionValues: [],
  },
};

const tagSeparator = ': ';

const AndonLundbeckResolveCallDialog: FunctionComponent<Properties> = ({ call, onClose }) => {
  const [t] = useTranslation();
  const { classes, cx } = useStyles();
  const utils = useUtils();

  const [runAndonListWorkOrderKeys, { loading, data }] = useLazyQuery<
    Schema.AndonListWorkOrderKeysQuery,
    Schema.AndonListWorkOrderKeysQueryVariables
  >(andonListWorkOrderKeys, {
    ssr: false,
    errorPolicy: 'ignore',
  });

  const [andonCompany] = data?.andon || [];
  const tags = andonCompany?.tags || [];
  const [key] = andonCompany?.workOrderKeys || [];

  const [assignedTag] = (call.tags || []).filter((tag): tag is NonNullable<typeof tag> => !!tag);

  const [baseMachine, baseUnit] = assignedTag?.value.split(tagSeparator) || [];

  const baseState = {
    problem: '',
    rootCause: '',
    solution: '',
    initials: '',
    selectedMachine: baseMachine,
    selectedUnit: baseUnit,
    // NOTE: These are known as error_code_1-3 in APIPRO - we embed it in the
    // message.
    symptom: '',
    cause: '',
    action: '',
    submitAttempt: false,
  };

  // TODO: Use useReducer instead - state management has become rather complex
  const [problem, setProblem] = useState(baseState.problem);
  const [rootCause, setRootCause] = useState(baseState.rootCause);
  const [solution, setSolution] = useState(baseState.solution);
  const [initials, setInitials] = useState(baseState.initials);
  const [selectedMachine = baseState.selectedMachine, setSelectedMachine] = useState(baseState.selectedMachine);
  const [selectedUnit = baseState.selectedUnit, setSelectedUnit] = useState(baseState.selectedUnit);
  const [symptom, setSymptom] = useState(baseState.symptom);
  const [cause, setCause] = useState(baseState.cause);
  const [action, setAction] = useState(baseState.action);
  const [submitAttempt, setSubmitAttempt] = useState(baseState.submitAttempt);

  const selectedTag =
    tags.find((tag) => tag.value === [selectedMachine, selectedUnit].join(tagSeparator)) || assignedTag;

  const onProblemChange = useCallback(
    guardedCallback((event) => {
      setSubmitAttempt(baseState.submitAttempt);
      return setProblem(event);
    }),
    [],
  );
  const onRootCauseChange = useCallback(
    guardedCallback((event) => {
      setSubmitAttempt(baseState.submitAttempt);
      return setRootCause(event);
    }),
    [],
  );
  const onSolutionChange = useCallback(
    guardedCallback((event) => {
      setSubmitAttempt(baseState.submitAttempt);
      return setSolution(event);
    }),
    [],
  );
  const onInitialsChange = useCallback(
    guardedCallback((event) => {
      setSubmitAttempt(baseState.submitAttempt);
      return setInitials(event);
    }),
    [],
  );
  const onMachineChange = useCallback(
    guardedCallback((event) => {
      setSubmitAttempt(baseState.submitAttempt);
      return setSelectedMachine(event);
    }),
    [],
  );
  const onUnitChange = useCallback(
    guardedCallback((event) => {
      setSubmitAttempt(baseState.submitAttempt);
      return setSelectedUnit(event);
    }),
    [],
  );
  const onSymptomChange = useCallback(
    guardedCallback((event) => {
      setSubmitAttempt(baseState.submitAttempt);
      return setSymptom(event);
    }),
    [],
  );
  const onCauseChange = useCallback(
    guardedCallback((event) => {
      setSubmitAttempt(baseState.submitAttempt);
      return setCause(event);
    }),
    [],
  );
  const onActionChange = useCallback(
    guardedCallback((event) => {
      setSubmitAttempt(baseState.submitAttempt);
      return setAction(event);
    }),
    [],
  );

  const onExited = useCallback(() => {
    const states: [Function, string][] = [
      [setProblem, baseState.problem],
      [setCause, baseState.cause],
      [setSolution, baseState.solution],
      [setInitials, baseState.initials],
      [setSelectedMachine, baseState.selectedMachine],
      [setSelectedUnit, baseState.selectedUnit],
      [setSymptom, baseState.symptom],
      [setCause, baseState.cause],
      [setAction, baseState.action],
    ];

    states.forEach(([setter, state]) => setter(() => state));
  }, []);

  const { companyId } = useContext(Context);

  useEffect(() => {
    if (!call.line || !companyId) {
      return;
    }

    return runAndonListWorkOrderKeys({
      variables: {
        companyId: [companyId],
        tagIds: [assignedTag.id],
        lineId: call.line.id,
      },
    });
  }, [assignedTag, call.line?.id]);

  const machineUnitMap = tags.reduce((acc, tag) => {
    const [machine, unit] = tag.value.split(tagSeparator);

    acc[machine] = acc[machine] || [];

    if (unit) {
      acc[machine].push(unit);
    }

    return acc;
  }, {} as Record<string, string[]>);

  const [release, { loading: releasing }] = useMutation<
    Schema.AndonCallReleaseMutation,
    Schema.AndonCallReleaseMutationVariables
  >(andonCallRelease);
  const [sendWorkOrder, { loading: sending }] = useMutation<
    Schema.AndonCallWorkOrderInitiateMutation,
    Schema.AndonCallWorkOrderInitiateMutationVariables
  >(andonCallWorkOrderInitiate);

  const callAction = call.action.take;
  if (!callAction || !companyId) {
    return null;
  }

  const problemLabel = t(['andon:call:lundbeck:problem'], { defaultValue: '1. Problem' });
  const rootCauseLabel = t(['andon:call:lundbeck:rootCause'], { defaultValue: '2. Root Cause' });
  const solutionLabel = t(['andon:call:lundbeck:solution'], { defaultValue: '3. Solution' });

  const textFields = [
    {
      id: 'problem',
      label: problemLabel,
      rows: 4,
      value: problem,
      onChange: onProblemChange,
      required: true,
      multiline: true,
      error: !problem.length,
    },
    {
      id: 'rootCause',
      label: rootCauseLabel,
      rows: 4,
      value: rootCause,
      onChange: onRootCauseChange,
      required: true,
      multiline: true,
      error: !rootCause.length,
    },
    {
      id: 'solution',
      label: solutionLabel,
      rows: 4,
      value: solution,
      onChange: onSolutionChange,
      required: true,
      multiline: true,
      error: !solution.length,
    },
    {
      id: 'initials',
      label: t(['shared:initials'], { defaultValue: 'Initials' }),
      rows: 1,
      value: initials,
      onChange: onInitialsChange,
      required: true,
      error: !initials.length,
    },
  ];

  const unitOptions = machineUnitMap[selectedMachine] || [];

  const site = call.line?.description?.startsWith('641') ? 'valbonne' : 'valby';
  const { symptomValues = [], causeValues = [], actionValues = [] } = errorCodes[site];

  const selectFields = [
    {
      id: 'symptom',
      label: t(['shared:symptom'], { defaultValue: 'Symptom' }),
      value: symptom,
      onChange: onSymptomChange,
      values: symptomValues,
    },
    {
      id: 'cause',
      label: t(['shared:cause'], { defaultValue: 'Cause' }),
      value: cause,
      onChange: onCauseChange,
      values: causeValues,
    },
    {
      id: 'action',
      label: t(['shared:action'], { defaultValue: 'Action' }),
      value: action,
      onChange: onActionChange,
      values: actionValues,
    },
    {
      id: 'machine',
      label: t(['apipro:machine'], { defaultValue: 'Machine' }),
      value: selectedMachine,
      onChange: onMachineChange,
      values: Object.keys(machineUnitMap),
    },
    {
      id: 'unit',
      label: t(['apipro:unit'], { defaultValue: 'Unit' }),
      value: unitOptions.length ? selectedUnit : '',
      onChange: onUnitChange,
      values: unitOptions,
      disabled: !unitOptions.length,
    },
  ];

  const details = [problemLabel, problem, '', rootCauseLabel, rootCause, '', solutionLabel, solution].join('\n');

  const isTooLong = HISTORY_NOTE_LIMIT <= details.length;
  const embeddedDetails = embed(details, { symptom, cause, action });

  const isValbonne = key?.key?.includes('DEP');
  const invalidFields = isValbonne ? !symptom : selectFields.some((field) => !field.value);

  const disableButton =
    textFields.some((field) => field.required && field.error) || invalidFields || !assignedTag || isTooLong;

  const summary = buildSummary(call.action, utils);

  return (
    <AndonCallDialogHeader onClose={onClose} onExited={onExited} maxWidth="md">
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            {callAction.author ? (
              <TextContentArea>
                <Typography variant="subtitle2">{t(['andon:call:takenBy'], { defaultValue: 'Taken by' })}</Typography>
                <Typography>{callAction.author}</Typography>
              </TextContentArea>
            ) : null}
            <TextContentArea>
              <Typography variant="subtitle2">{t(['shared:summary'], { defaultValue: 'Summary' })}</Typography>
              <Typography>{summary}</Typography>
            </TextContentArea>
            {textFields.map(({ id, label, rows, value, onChange, required, multiline, error }, i) => {
              return (
                <TextContentArea key={id}>
                  <FormControl variant="filled" fullWidth>
                    <TextField
                      id={id}
                      label={label}
                      rows={rows}
                      autoFocus={!i}
                      variant="outlined"
                      multiline={multiline}
                      onChange={onChange}
                      value={value}
                      required={required}
                      error={submitAttempt && error}
                    />
                  </FormControl>
                </TextContentArea>
              );
            })}
          </Grid>
          <Grid item xs={6}>
            {selectFields.map(({ id, label, value, onChange, values, disabled }) => {
              return (
                <TextContentArea key={id}>
                  <FormControl variant="filled" fullWidth>
                    <InputLabel>{label}</InputLabel>
                    <Select variant="filled" value={value} onChange={onChange} disabled={!!disabled}>
                      {values.map((item) => (
                        <MenuItem key={item.toLowerCase().trim()} value={item}>
                          {item}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </TextContentArea>
              );
            })}
            <TextContentArea>
              <FormControl variant="filled" fullWidth focused>
                <TextField
                  label={t(['andon:apipro:key'], { defaultValue: 'Key' })}
                  value={
                    loading
                      ? t(['andon:fetchingKeys'], { defaultValue: 'Fetching keys...' })
                      : key?.key || t(['shared:notAvailable'], { defaultValue: 'N/A' })
                  }
                  disabled
                  variant="outlined"
                />
              </FormControl>
            </TextContentArea>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary" className={classes.hideWhileSmall}>
          {t(['shared:close'], { defaultValue: 'Close' })}
        </Button>
        <Button
          onClick={async () => {
            return release({
              variables: {
                companyId,
                id: call.id,
              },
            });
          }}
          color="primary"
          variant="contained"
          disabled={releasing}
          className={cx(classes.buttonWithIconLeft, classes.buttonNegative)}
        >
          {releasing ? <CircularProgress className={cx(classes.iconSizeLeft)} /> : <CloseIcon />}
          {t(['shared:release'], { defaultValue: 'Release' })}
        </Button>
        <Button
          onClick={async () => {
            if (!selectedTag) {
              return;
            }

            return sendWorkOrder({
              variables: {
                companyId,
                callId: call.id,
                tagId: selectedTag.id,
                author: initials,
                details: embeddedDetails.valueOf(),
              },
            });
          }}
          color="primary"
          variant="contained"
          disabled={sending || disableButton}
          className={classes.buttonWithIconRight}
        >
          {t(['andon:apiProCall:resolve'], { defaultValue: 'Send Work Order' })}
          {sending ? <CircularProgress className={cx(classes.iconSizeRight)} /> : <SendIcon />}
        </Button>
      </DialogActions>
    </AndonCallDialogHeader>
  );
};

export default AndonLundbeckResolveCallDialog;
